import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Users, Plus, Edit, Trash2, Shield, UserPlus, Loader2, UserCheck,
  LayoutDashboard, GraduationCap, Briefcase, Calculator, BookOpen, 
  Bus, Building2, Calendar, Award, Bell, Settings, ShoppingBag
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  LayoutDashboard, GraduationCap, Users, Briefcase, Calculator, 
  BookOpen, Bus, Building2, Calendar, Award, Bell, Settings, ShoppingBag
};

const StaffRolesPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [staff, setStaff] = useState([]);
  const [presets, setPresets] = useState([]);
  
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Bulk selection state
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [bulkRoleId, setBulkRoleId] = useState('');
  
  // Individual staff role edit
  const [editStaffRoleDialogOpen, setEditStaffRoleDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [editStaffRoleId, setEditStaffRoleId] = useState('');
  
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    modules: [],
    color: '#6366F1'
  });
  
  const [staffForm, setStaffForm] = useState({
    name: '',
    email: '',
    phone: '',
    role_id: '',
    password: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [rolesRes, modulesRes, staffRes, presetsRes] = await Promise.all([
        axios.get(`${API_URL}/api/staff-roles/`, { headers }),
        axios.get(`${API_URL}/api/staff-roles/modules`, { headers }),
        axios.get(`${API_URL}/api/staff-roles/staff`, { headers }),
        axios.get(`${API_URL}/api/staff-roles/presets`, { headers })
      ]);
      
      setRoles(rolesRes.data);
      setModules(modulesRes.data);
      setStaff(staffRes.data);
      setPresets(presetsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async () => {
    if (!roleForm.name || roleForm.modules.length === 0) {
      toast.error('Please fill name and select at least one module');
      return;
    }
    
    setSubmitting(true);
    try {
      if (editingRole) {
        await axios.put(`${API_URL}/api/staff-roles/${editingRole.id}`, roleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Role updated successfully');
      } else {
        await axios.post(`${API_URL}/api/staff-roles`, roleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Role created successfully');
      }
      
      setRoleDialogOpen(false);
      resetRoleForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateStaff = async () => {
    if (!staffForm.name || !staffForm.email || !staffForm.role_id || !staffForm.password) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/staff-roles/staff`, staffForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Staff created successfully');
      setStaffDialogOpen(false);
      resetStaffForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create staff');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/staff-roles/${roleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Role deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  // Toggle staff selection for bulk assign
  const toggleStaffSelection = (staffId) => {
    setSelectedStaffIds(prev => 
      prev.includes(staffId) 
        ? prev.filter(id => id !== staffId)
        : [...prev, staffId]
    );
  };

  // Select all staff without roles
  const selectAllWithoutRole = () => {
    const noRoleStaff = staff.filter(s => !s.staff_role_id).map(s => s.id);
    setSelectedStaffIds(noRoleStaff);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedStaffIds([]);
  };

  // Bulk assign role
  const handleBulkAssignRole = async () => {
    if (selectedStaffIds.length === 0) {
      toast.error('Please select at least one staff member');
      return;
    }
    if (!bulkRoleId) {
      toast.error('Please select a role to assign');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/staff-roles/staff/bulk-assign-role`, {
        staff_ids: selectedStaffIds,
        role_id: bulkRoleId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setBulkAssignDialogOpen(false);
      setSelectedStaffIds([]);
      setBulkRoleId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign roles');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle individual staff role change
  const handleEditStaffRole = async () => {
    if (!editingStaff || !editStaffRoleId) {
      toast.error('Please select a role');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/staff-roles/staff/${editingStaff.id}/role?role_id=${editStaffRoleId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Role updated for ${editingStaff.name}`);
      setEditStaffRoleDialogOpen(false);
      setEditingStaff(null);
      setEditStaffRoleId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditStaffRoleDialog = (staffMember) => {
    setEditingStaff(staffMember);
    setEditStaffRoleId(staffMember.staff_role_id || '');
    setEditStaffRoleDialogOpen(true);
  };

  const resetRoleForm = () => {
    setRoleForm({ name: '', description: '', modules: [], color: '#6366F1' });
    setEditingRole(null);
  };

  const resetStaffForm = () => {
    setStaffForm({ name: '', email: '', phone: '', role_id: '', password: '' });
  };

  const toggleModule = (moduleId) => {
    setRoleForm(prev => ({
      ...prev,
      modules: prev.modules.includes(moduleId)
        ? prev.modules.filter(m => m !== moduleId)
        : [...prev.modules, moduleId]
    }));
  };

  if (loading) {
    return (
      <DashboardLayout title="Staff Roles & Permissions">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Staff Roles & Permissions">
      <div className="space-y-6">
        <Tabs defaultValue="roles">
          <TabsList>
            <TabsTrigger value="roles">Roles</TabsTrigger>
            <TabsTrigger value="staff">Staff Members</TabsTrigger>
          </TabsList>

          {/* Roles Tab */}
          <TabsContent value="roles" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Staff Roles</h2>
                <p className="text-muted-foreground">Create and manage custom staff roles with module-wise access</p>
              </div>
              <Button onClick={() => { resetRoleForm(); setRoleDialogOpen(true); }} data-testid="create-role-btn">
                <Plus className="h-4 w-4 mr-2" /> Create Custom Role
              </Button>
            </div>

            {/* Preset Roles */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" /> Preset Roles
                </CardTitle>
                <CardDescription>Ready-to-use roles with predefined permissions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {presets.map((role, idx) => (
                    <motion.div
                      key={role.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                    >
                      <Card className="border-l-4" style={{ borderLeftColor: role.color }}>
                        <CardContent className="pt-4">
                          <h4 className="font-semibold">{role.name}</h4>
                          <p className="text-xs text-muted-foreground mb-2">{role.description}</p>
                          <div className="flex flex-wrap gap-1">
                            {role.modules.map(m => (
                              <Badge key={m} variant="secondary" className="text-xs">{m}</Badge>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Custom Roles */}
            <Card>
              <CardHeader>
                <CardTitle>Custom Roles</CardTitle>
                <CardDescription>Roles created by your organization</CardDescription>
              </CardHeader>
              <CardContent>
                {roles.filter(r => !r.is_preset).length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No custom roles yet. Create one to get started.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {roles.filter(r => !r.is_preset).map((role, idx) => (
                      <Card key={role.id} className="border-l-4" style={{ borderLeftColor: role.color }}>
                        <CardContent className="pt-4">
                          <div className="flex justify-between">
                            <h4 className="font-semibold">{role.name}</h4>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => {
                                setEditingRole(role);
                                setRoleForm({
                                  name: role.name,
                                  description: role.description || '',
                                  modules: role.modules,
                                  color: role.color
                                });
                                setRoleDialogOpen(true);
                              }}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteRole(role.id)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mb-2">{role.description}</p>
                          <div className="flex flex-wrap gap-1">
                            {role.modules.map(m => (
                              <Badge key={m} variant="secondary" className="text-xs">{m}</Badge>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Staff Tab */}
          <TabsContent value="staff" className="space-y-6">
            <div className="flex justify-between items-center flex-wrap gap-4">
              <div>
                <h2 className="text-xl font-bold">Staff Members</h2>
                <p className="text-muted-foreground">Manage staff with assigned roles</p>
              </div>
              <div className="flex gap-2">
                {selectedStaffIds.length > 0 && (
                  <Button variant="default" onClick={() => setBulkAssignDialogOpen(true)} data-testid="bulk-assign-btn">
                    <UserCheck className="h-4 w-4 mr-2" /> Assign Role ({selectedStaffIds.length})
                  </Button>
                )}
                <Button onClick={() => { resetStaffForm(); setStaffDialogOpen(true); }} data-testid="add-staff-btn">
                  <UserPlus className="h-4 w-4 mr-2" /> Add Staff
                </Button>
              </div>
            </div>

            {/* Bulk Selection Controls */}
            {staff.length > 0 && (
              <div className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg">
                <Checkbox 
                  checked={selectedStaffIds.length === staff.length && staff.length > 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedStaffIds(staff.map(s => s.id));
                    } else {
                      setSelectedStaffIds([]);
                    }
                  }}
                />
                <span className="text-sm text-muted-foreground">Select All</span>
                <Button variant="link" size="sm" onClick={selectAllWithoutRole} className="text-xs">
                  Select Without Role
                </Button>
                {selectedStaffIds.length > 0 && (
                  <Button variant="link" size="sm" onClick={clearSelection} className="text-xs text-red-500">
                    Clear Selection
                  </Button>
                )}
                <span className="ml-auto text-sm">
                  {selectedStaffIds.length} of {staff.length} selected
                </span>
              </div>
            )}

            <Card>
              <CardContent className="pt-6">
                {staff.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No staff members yet.</p>
                ) : (
                  <div className="space-y-3">
                    {staff.map((s, idx) => (
                      <motion.div
                        key={s.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-colors ${
                          selectedStaffIds.includes(s.id) ? 'bg-primary/10 border border-primary' : 'bg-muted/50 hover:bg-muted'
                        }`}
                        onClick={() => toggleStaffSelection(s.id)}
                      >
                        <div className="flex items-center gap-4">
                          <Checkbox 
                            checked={selectedStaffIds.includes(s.id)}
                            onCheckedChange={() => toggleStaffSelection(s.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: s.role_color || '#6366F1' }}>
                            <Users className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium">{s.name}</p>
                            <p className="text-sm text-muted-foreground">{s.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {s.role_name ? (
                            <Badge style={{ backgroundColor: s.role_color || '#6366F1' }} className="text-white">
                              {s.role_name}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-orange-600 border-orange-300">
                              No Role
                            </Badge>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={(e) => { e.stopPropagation(); openEditStaffRoleDialog(s); }}
                            data-testid={`edit-role-${s.id}`}
                            className="text-blue-600 border-blue-300 hover:bg-blue-50"
                          >
                            <Edit className="h-4 w-4 mr-1" /> Edit Role
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Individual Staff Role Edit Dialog */}
        <Dialog open={editStaffRoleDialogOpen} onOpenChange={setEditStaffRoleDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Staff Role</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {editingStaff && (
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-primary">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium">{editingStaff.name}</p>
                    <p className="text-sm text-muted-foreground">{editingStaff.email}</p>
                  </div>
                </div>
              )}
              
              <div>
                <Label>Select Role *</Label>
                <Select value={editStaffRoleId} onValueChange={setEditStaffRoleId}>
                  <SelectTrigger data-testid="edit-staff-role-select">
                    <SelectValue placeholder="Choose a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="px-2 py-1 text-xs text-muted-foreground font-semibold">Preset Roles</div>
                    {presets.map(preset => (
                      <SelectItem key={preset.id} value={preset.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: preset.color }} />
                          {preset.name}
                        </div>
                      </SelectItem>
                    ))}
                    {roles.filter(r => !r.is_preset).length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs text-muted-foreground font-semibold mt-2">Custom Roles</div>
                        {roles.filter(r => !r.is_preset).map(role => (
                          <SelectItem key={role.id} value={role.id}>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: role.color }} />
                              {role.name}
                            </div>
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditStaffRoleDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleEditStaffRole} disabled={submitting || !editStaffRoleId}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Edit className="h-4 w-4 mr-2" />}
                Update Role
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Assign Role Dialog */}
        <Dialog open={bulkAssignDialogOpen} onOpenChange={setBulkAssignDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Bulk Assign Role</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Assign a role to <strong>{selectedStaffIds.length}</strong> selected staff members
              </p>
              
              <div>
                <Label>Select Role *</Label>
                <Select value={bulkRoleId} onValueChange={setBulkRoleId}>
                  <SelectTrigger data-testid="bulk-role-select">
                    <SelectValue placeholder="Choose a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="px-2 py-1 text-xs text-muted-foreground font-semibold">Preset Roles</div>
                    {presets.map(preset => (
                      <SelectItem key={preset.id} value={preset.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: preset.color }} />
                          {preset.name}
                        </div>
                      </SelectItem>
                    ))}
                    {roles.filter(r => !r.is_preset).length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs text-muted-foreground font-semibold mt-2">Custom Roles</div>
                        {roles.filter(r => !r.is_preset).map(role => (
                          <SelectItem key={role.id} value={role.id}>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: role.color }} />
                              {role.name}
                            </div>
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-muted/50 p-3 rounded-lg max-h-40 overflow-y-auto">
                <p className="text-xs font-medium mb-2">Selected Staff:</p>
                <div className="flex flex-wrap gap-1">
                  {staff.filter(s => selectedStaffIds.includes(s.id)).map(s => (
                    <Badge key={s.id} variant="secondary" className="text-xs">{s.name}</Badge>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkAssignDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleBulkAssignRole} disabled={submitting || !bulkRoleId}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserCheck className="h-4 w-4 mr-2" />}
                Assign Role
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create/Edit Role Dialog */}
        <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRole ? 'Edit Role' : 'Create Custom Role'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Role Name *</Label>
                  <Input
                    value={roleForm.name}
                    onChange={(e) => setRoleForm({...roleForm, name: e.target.value})}
                    placeholder="e.g., Senior Accountant"
                    data-testid="role-name-input"
                  />
                </div>
                <div>
                  <Label>Color</Label>
                  <Input
                    type="color"
                    value={roleForm.color}
                    onChange={(e) => setRoleForm({...roleForm, color: e.target.value})}
                    className="h-10 w-full"
                  />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({...roleForm, description: e.target.value})}
                  placeholder="Brief description of this role"
                />
              </div>
              <div>
                <Label>Select Modules *</Label>
                <p className="text-xs text-muted-foreground mb-2">Choose which modules this role can access</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-60 overflow-y-auto p-2 border rounded-lg">
                  {modules.map(module => (
                    <div
                      key={module.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        roleForm.modules.includes(module.id) 
                          ? 'bg-primary/10 border border-primary' 
                          : 'bg-muted/50 hover:bg-muted'
                      }`}
                      onClick={() => toggleModule(module.id)}
                    >
                      <Checkbox checked={roleForm.modules.includes(module.id)} />
                      <span className="text-sm">{module.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateRole} disabled={submitting} data-testid="save-role-btn">
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingRole ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Staff Dialog */}
        <Dialog open={staffDialogOpen} onOpenChange={setStaffDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Staff Member</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={staffForm.name}
                  onChange={(e) => setStaffForm({...staffForm, name: e.target.value})}
                  placeholder="Full name"
                  data-testid="staff-name-input"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={staffForm.email}
                  onChange={(e) => setStaffForm({...staffForm, email: e.target.value})}
                  placeholder="email@example.com"
                  data-testid="staff-email-input"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={staffForm.phone}
                  onChange={(e) => setStaffForm({...staffForm, phone: e.target.value})}
                  placeholder="Phone number"
                />
              </div>
              <div>
                <Label>Role *</Label>
                <Select value={staffForm.role_id} onValueChange={(v) => setStaffForm({...staffForm, role_id: v})}>
                  <SelectTrigger data-testid="staff-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">Preset Roles</div>
                    {presets.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                    {roles.filter(r => !r.is_preset).length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs font-semibold text-muted-foreground mt-2">Custom Roles</div>
                        {roles.filter(r => !r.is_preset).map(r => (
                          <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={staffForm.password}
                  onChange={(e) => setStaffForm({...staffForm, password: e.target.value})}
                  placeholder="Set password"
                  data-testid="staff-password-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStaffDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateStaff} disabled={submitting} data-testid="save-staff-btn">
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Add Staff
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default StaffRolesPage;
